import { useCallback, useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { buildQuoteCreateRoute, buildQuoteEditRoute } from '../../constants/auth';
import {
  ACCEPT_QUOTE,
  CLICK_ACTION,
  DOWNLOAD_PDF_QUOTE,
  EDIT_QUOTE,
  NEW_CLIENT_QUOTE,
  NEW_QUOTE,
  SOURCE_PANEL,
  UNACCEPT_QUOTE
} from '../../constants/tagManager';
import type { IQuote } from '../../interfaces/quotes';
import QuotesService from '../../services/api/quotes/quotes';
import TagManagerService from '../../services/gtm/tagManagerService';
import { TRANSLATIONS_PROSPECTS } from '../../translations/prospects';
import type { StatusVariant } from '../../types/globals';
import { openOrDownloadPdf } from '../../utils/utils';
import { useDownloadQuotePdf } from '../useDownloadQuotePdf/useDownloadQuotePdf';
import { useUser } from '../useUser/useUser';

export interface IQuoteFlowReturnTarget {
  path: string;
  state?: Record<string, unknown>;
}

export const useQuoteActions = (
  initialQuotes: IQuote[],
  options?: {
    onAcceptSuccess?: () => void;
    onUnacceptSuccess?: () => void;
    returnTo?: IQuoteFlowReturnTarget;
    fromClientProfile?: boolean;
  }
) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { brokerId } = useUser();

  const [localQuotes, setLocalQuotes] = useState<IQuote[]>(initialQuotes);
  const [toast, setToast] = useState<{ open: boolean; message: string; variant: StatusVariant }>({
    open: false,
    message: '',
    variant: 'success'
  });
  const [unacceptTarget, setUnacceptTarget] = useState<IQuote | null>(null);
  const [acceptingQuoteId, setAcceptingQuoteId] = useState<number | null>(null);
  const { downloadAndOpenPdf, downloadingQuoteId } = useDownloadQuotePdf();

  useEffect(() => {
    setLocalQuotes(initialQuotes);
  }, [initialQuotes]);

  const returnTo = options?.returnTo;
  const fromClientProfile = options?.fromClientProfile ?? false;

  const handleNewQuote = useCallback(
    (prospectId: number) => {
      if (fromClientProfile) {
        TagManagerService.pushEvent(CLICK_ACTION, NEW_CLIENT_QUOTE);
      } else {
        TagManagerService.pushEvent(CLICK_ACTION, NEW_QUOTE);
      }

      if (returnTo) {
        navigate(buildQuoteCreateRoute(prospectId), { state: { returnTo } });
      } else {
        navigate(buildQuoteCreateRoute(prospectId));
      }
    },
    [navigate, returnTo, fromClientProfile]
  );

  const handleEditQuote = useCallback(
    (prospectId: number, quoteId: number) => {
      const quote = localQuotes.find((q) => q.id === quoteId);
      if (quote) {
        TagManagerService.pushEvent(CLICK_ACTION, EDIT_QUOTE);
        navigate(buildQuoteEditRoute(prospectId, quoteId), { state: { quote, ...(returnTo ? { returnTo } : {}) } });
      }
    },
    [localQuotes, navigate, returnTo]
  );

  const showDownloadError = useCallback(() => {
    setToast({
      open: true,
      message: TRANSLATIONS_PROSPECTS.detail.quotationCard.downloadPdfError,
      variant: 'warning'
    });
  }, []);

  const handleDownloadQuote = useCallback(
    async (prospectId: number, quoteId: number) => {
      TagManagerService.pushEvent(CLICK_ACTION, DOWNLOAD_PDF_QUOTE, SOURCE_PANEL);
      const localFile = localQuotes.find((q) => q.id === quoteId)?.files.find((f) => f.fileType === 'quote');
      if (localFile?.documentUrl) {
        if ((await openOrDownloadPdf(localFile.documentUrl)) === 'failed') showDownloadError();
        return;
      }

      if (!brokerId) return;

      const outcome = await downloadAndOpenPdf(brokerId, prospectId, quoteId);
      if (outcome === 'failed') showDownloadError();
    },
    [brokerId, downloadAndOpenPdf, localQuotes, showDownloadError]
  );

  const handleAcceptQuote = useCallback(
    async (prospectId: number, quoteId: number) => {
      const quote = localQuotes.find((q) => q.id === quoteId);
      if (!quote) return;

      if (quote.status === 'accepted') {
        setUnacceptTarget(quote);
      } else {
        if (!brokerId) return;

        setAcceptingQuoteId(quoteId);
        try {
          await QuotesService.updateQuoteStatus(brokerId, prospectId, quoteId, { status: 'accepted' });
          setLocalQuotes((prev) => prev.map((q) => (q.id === quoteId ? { ...q, status: 'accepted' } : q)));
          TagManagerService.pushEvent(CLICK_ACTION, ACCEPT_QUOTE);
          setToast({
            open: true,
            message: t(TRANSLATIONS_PROSPECTS.detail.quotationCard.quoteAcceptedToast, { quoteName: quote.name }),
            variant: 'success'
          });
          options?.onAcceptSuccess?.();
        } catch {
          setToast({
            open: true,
            message: TRANSLATIONS_PROSPECTS.newProspectModal.toast.error,
            variant: 'warning'
          });
        } finally {
          setAcceptingQuoteId(null);
        }
      }
    },
    [localQuotes, brokerId, t, options]
  );

  const handleConfirmUnaccept = useCallback(
    async (prospectId: number) => {
      if (!unacceptTarget) return;
      if (!brokerId) {
        setUnacceptTarget(null);
        return;
      }
      try {
        await QuotesService.updateQuoteStatus(brokerId, prospectId, unacceptTarget.id, { status: 'created' });
        setLocalQuotes((prev) => prev.map((q) => (q.id === unacceptTarget.id ? { ...q, status: 'created' } : q)));
        options?.onUnacceptSuccess?.();
        TagManagerService.pushEvent(CLICK_ACTION, UNACCEPT_QUOTE);
      } catch {
        setToast({
          open: true,
          message: TRANSLATIONS_PROSPECTS.newProspectModal.toast.error,
          variant: 'warning'
        });
      } finally {
        setUnacceptTarget(null);
      }
    },
    [unacceptTarget, brokerId, options]
  );

  const handleCancelUnaccept = useCallback(() => {
    setUnacceptTarget(null);
  }, []);

  return {
    localQuotes,
    toast,
    setToast,
    unacceptTarget,
    acceptingQuoteId,
    downloadingQuoteId,
    handleNewQuote,
    handleEditQuote,
    handleDownloadQuote,
    handleAcceptQuote,
    handleConfirmUnaccept,
    handleCancelUnaccept
  };
};
